import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip, { QNA_ANSWER_STATUS_CHIP } from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchType: '',
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const getUserId = (row) => row?.user?.userId ?? row?.userId
const getDisplayValue = (value) => value ?? '-'

export default function Qnas({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const deleteInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const silentListRefreshRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/productQna/getProductQnaList', { page: curPage, offset: offset, adminId: adminId, role: role,
                searchText: searchQuery, searchSelect: searchSelect })

            if (data.code === 1) {
                setTotalCount(data.data?.count || 0)
                setData(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '상품 문의 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '상품 문의 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isBusy = Boolean(actionLoading)

    const updateSearchSelect = (e) => {
        if (isBusy) {
            return
        }

        const { name, value } = e.target

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchSelect(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
        }

        if (searchQuery !== trimmedSearchText) {
            setSearchQuery(trimmedSearchText)
            return
        }

        if (curPage === 1) {
            getData(false)
        }
    }

    const applySearchValue = (value) => {
        const nextSearchText = String(value ?? '').trim()

        if (isBusy || !nextSearchText) {
            return
        }

        setSearchText(nextSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }

        if (searchQuery !== nextSearchText) {
            setSearchQuery(nextSearchText)
            return
        }

        if (curPage === 1) {
            getData(false)
        }
    }

    const copyTextToClipboard = async (text) => {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return
        }

        if (typeof document === 'undefined') {
            throw new Error('clipboard is not available')
        }

        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.setAttribute('readonly', '')
        textArea.style.position = 'fixed'
        textArea.style.top = '-9999px'
        document.body.appendChild(textArea)
        textArea.select()

        try {
            const copied = document.execCommand('copy')
            if (!copied) {
                throw new Error('copy command failed')
            }
        } finally {
            document.body.removeChild(textArea)
        }
    }

    const handleUserIdClick = async (userId) => {
        const nextSearchText = String(userId ?? '').trim()

        if (isBusy || !nextSearchText) {
            return
        }

        applySearchValue(nextSearchText)

        try {
            await copyTextToClipboard(nextSearchText)
            setOpenSnackbar(true)
            setSeveritySnackbar('success')
            setMessageSnackbar(`userId ${nextSearchText} 복사되었습니다.`)
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('userId 복사에 실패했습니다.')
        }
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current.has(id)) {
            return
        }

        try {
            deleteInFlightRef.current.add(id)
            setActionLoading({ type: 'delete', id })

            const { data: response } = await API.post('/api/v1/admin/productQna/delete', { productQnaId: id })

            if (response.code === 1) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('상품 문의를 삭제했습니다.')

                if (data.length === 1 && curPage > 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData()
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('상품 문의를 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current.delete(id)
            setActionLoading(null)
        }
    }

    // 검색어와 선택 필터가 기본값에서 벗어났는지 확인해 빈 상태 안내를 구분한다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? ''

            return String(currentValue) !== String(defaultValue)
        })
    }

    // 목록 행이 없을 때 오류, 필터 결과 없음, 기본 빈 목록을 명확히 보여준다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="상품 문의 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={isBusy}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 상품 문의가 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="접수된 상품 문의가 없습니다."
                description="상품 문의가 접수되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>상품 문의 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="유저ID/상품/이름/내용"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={isBusy}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="답변상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                                disabled={isBusy}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value='0'>미답변</MenuItem>
                                <MenuItem value='1'>답변</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    {/* 테이블 */}
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 900 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>상품</TableCell>
                                <TableCell>userId</TableCell>
                                <TableCell>이름</TableCell>
                                <TableCell>내용</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell>답변일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? data.map((row) => {
                                const userId = getUserId(row)

                                return (
                                    <TableRow key={row.productQnaId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell>{row.product.title}</TableCell>
                                        <TableCell>
                                            <Button
                                                type="button"
                                                size="small"
                                                onClick={() => handleUserIdClick(userId)}
                                                disabled={isBusy || !userId}
                                            >
                                                {getDisplayValue(userId)}
                                            </Button>
                                        </TableCell>
                                        <TableCell>{row.user.username}</TableCell>
                                        <TableCell className="listTableLimitText">{row.content}</TableCell>
                                        <TableCell><StatusChip status={row.status} config={QNA_ANSWER_STATUS_CHIP} /></TableCell>
                                        <TableCell>{row.regTime}</TableCell>
                                        <TableCell>{row.answerTime}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup disabled={isBusy}>
                                                <EditIcon path={`productQna`} id={row.productQnaId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.productQnaId}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                )
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
